import { TrendingUp, TrendingDown, Settings2 } from 'lucide-react';
import { Link } from 'react-router-dom';

import { useLatestData, useLatestSignal, useHistory, useConfig } from '@/hooks/useApi';
import { formatCurrency, formatPrice, formatScore } from '@/lib/format';
import type { Signal } from '@/types';

interface SymbolCardProps {
	symbol: string;
}

const typeConfig: Record<Signal['type'], { label: string; color: string }> = {
	ENTRY: { label: 'Entry', color: 'bg-success/10 text-success border-success/20' },
	EXIT: { label: 'Exit', color: 'bg-danger/10 text-danger border-danger/20' },
	STOP_LOSS: { label: 'Stop Loss', color: 'bg-danger/20 text-danger border-danger/30' },
	TAKE_PROFIT: { label: 'Take Profit', color: 'bg-success/20 text-success border-success/30' },
	ADJUSTMENT: { label: 'Adjustment', color: 'bg-info/10 text-info border-info/20' },
	HOLD: { label: 'Hold', color: 'bg-warning/10 text-warning border-warning/20' },
	NO_ACTION: { label: 'No Action', color: 'bg-text-muted/10 text-text-muted border-text-muted/20' },
	TRAILING_STOP: { label: 'Trail Stop', color: 'bg-danger/20 text-danger border-danger/30' },
	BLOCKED: { label: 'Blocked', color: 'bg-warning/20 text-warning border-warning/30' }
};

export function SymbolCard({ symbol }: SymbolCardProps) {
	const { data: latestData } = useLatestData(symbol);
	const { data: latestSignal } = useLatestSignal(symbol);
	const { data: historyData } = useHistory(symbol, 100);
	const { data: config } = useConfig();

	const displaySymbol = symbol.replace('PERP_', '').replace('_USDC', '');
	const price = latestData?.indicators?.candle?.close;
	const volume = latestData?.indicators?.candle?.volume;
	const taScore = latestSignal?.signal?.taScore;
	const lastSignal = latestSignal?.signal;
	const atrValue = latestData?.indicators?.atr?.value;
	const atrPct = atrValue && price ? (atrValue / price) * 100 : undefined;

	// Get symbol config
	const symbolConfig = config?.symbolConfigs?.[symbol];

	// Calculate 24h price change
	const calculate24hChange = () => {
		if (!historyData?.data || historyData.data.length < 2 || !price) return null;

		// Find data point closest to 24h ago
		const now = Date.now();
		const twentyFourHoursAgo = now - 24 * 60 * 60 * 1000;

		// Sort by timestamp descending and find first point >= 24h ago
		const sortedData = [...historyData.data].sort((a, b) => b.timestamp - a.timestamp);
		const dayAgoPoint = sortedData.find((d) => d.timestamp <= twentyFourHoursAgo);

		if (!dayAgoPoint) return null;

		const oldPrice = dayAgoPoint.indicators.candle?.close;
		if (!oldPrice) return null;

		const change = ((price - oldPrice) / oldPrice) * 100;
		return change;
	};

	const priceChange24h = calculate24hChange();

	const getTaScoreColor = (score: number) => {
		if (score >= 2) return 'text-success';
		if (score >= 0.5) return 'text-success/80';
		if (score >= -0.5) return 'text-text-muted';
		if (score >= -2) return 'text-danger/80';
		return 'text-danger';
	};

	const getTaScoreLabel = (score: number) => {
		if (score >= 2) return 'Strong Buy';
		if (score >= 0.5) return 'Buy';
		if (score >= -0.5) return 'Neutral';
		if (score >= -2) return 'Sell';
		return 'Strong Sell';
	};

	return (
		<Link
			to={`/markets/${encodeURIComponent(symbol)}`}
			className="card group block relative hover:border-primary/50 transition-colors"
		>
			<div className="pr-10">
				<div className="flex items-center justify-between">
					<h3 className="text-lg font-bold text-text">{displaySymbol}</h3>
					{lastSignal && (
						<span
							className={`text-xs font-medium px-2 py-1 rounded border ${typeConfig[lastSignal.type].color}`}
						>
							{typeConfig[lastSignal.type].label}
						</span>
					)}
				</div>

				<div className="mt-4 space-y-3">
					<div>
						<p className="text-xs text-text-muted uppercase tracking-wide">Price</p>
						<div className="flex items-center gap-2">
							<p className="text-2xl font-bold text-text">
								{price !== undefined ? formatPrice(price) : '-'}
							</p>
							{priceChange24h !== null && (
								<div
									className={`flex items-center gap-1 text-sm ${priceChange24h >= 0 ? 'text-success' : 'text-danger'}`}
								>
									{priceChange24h >= 0 ? (
										<TrendingUp className="w-4 h-4" />
									) : (
										<TrendingDown className="w-4 h-4" />
									)}
									<span>
										{priceChange24h >= 0 ? '+' : ''}
										{priceChange24h.toFixed(2)}%
									</span>
								</div>
							)}
						</div>
					</div>

					<div className="grid grid-cols-3 gap-4">
						<div>
							<p className="text-xs text-text-muted uppercase tracking-wide">TA Score</p>
							<div className="flex flex-col">
								<p
									className={`text-xl font-bold ${taScore !== undefined ? getTaScoreColor(taScore) : 'text-text-muted'}`}
								>
									{taScore !== undefined ? formatScore(taScore) : '-'}
								</p>
								{taScore !== undefined && (
									<span className="text-xs text-text-muted truncate">
										{getTaScoreLabel(taScore)}
									</span>
								)}
							</div>
						</div>
						<div>
							<p className="text-xs text-text-muted uppercase tracking-wide">ATR%</p>
							<div className="flex flex-col">
								<p
									className={`text-xl font-bold ${
										atrPct !== undefined &&
										symbolConfig?.MIN_ATR_PCT !== undefined &&
										atrPct < (symbolConfig.MIN_ATR_PCT || 0) * 100
											? 'text-warning'
											: 'text-text'
									}`}
								>
									{atrPct !== undefined ? `${atrPct.toFixed(2)}%` : '-'}
								</p>
								{symbolConfig?.MIN_ATR_PCT !== undefined && atrPct !== undefined && (
									<span className="text-xs text-text-muted truncate">
										Min {((symbolConfig.MIN_ATR_PCT || 0) * 100).toFixed(2)}%
									</span>
								)}
							</div>
						</div>
						<div>
							<p className="text-xs text-text-muted uppercase tracking-wide">Volume (USD)</p>
							<p className="text-lg font-bold text-text">
								{volume !== undefined && price !== undefined
									? formatCurrency(volume * price, 0)
									: '-'}
							</p>
						</div>
					</div>

					{/* Configuration Summary */}
					{symbolConfig && (
						<div className="pt-3 border-t border-border">
							<div className="flex items-center gap-1 mb-2">
								<Settings2 className="w-3 h-3 text-text-muted" />
								<p className="text-xs text-text-muted uppercase tracking-wide">Config</p>
							</div>
							<div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
								<div className="flex justify-between">
									<span className="text-text-muted">ATR&times;SL:</span>
									<span className="text-danger font-medium">
										{symbolConfig.ATR_SL_MULTIPLIER.toFixed(2)}
									</span>
								</div>
								<div className="flex justify-between">
									<span className="text-text-muted">ATR&times;TP:</span>
									<span className="text-success font-medium">
										{symbolConfig.ATR_TP_MULTIPLIER.toFixed(2)}
									</span>
								</div>
								<div className="flex justify-between">
									<span className="text-text-muted">VWAP:</span>
									<span className="text-text">{symbolConfig.VWAP_MULTIPLIER.toFixed(3)}</span>
								</div>
								<div className="flex justify-between">
									<span className="text-text-muted">BB:</span>
									<span className="text-text">{symbolConfig.BBANDS_MULTIPLIER.toFixed(3)}</span>
								</div>
								<div className="flex justify-between">
									<span className="text-text-muted">RSI:</span>
									<span className="text-text">{symbolConfig.RSI_MULTIPLIER.toFixed(3)}</span>
								</div>
								<div className="flex justify-between">
									<span className="text-text-muted">Lev:</span>
									<span className="text-text">{symbolConfig.MAX_LEVERAGE}x</span>
								</div>
								{symbolConfig.MIN_ATR_PCT !== undefined && (
									<div className="flex justify-between">
										<span className="text-text-muted">Min ATR%:</span>
										<span className="text-text">
											{(symbolConfig.MIN_ATR_PCT * 100).toFixed(2)}%
										</span>
									</div>
								)}
							</div>
						</div>
					)}
				</div>
			</div>
		</Link>
	);
}
